import * as Sentry from '@sentry/react'
import { API_BASE_URL, X_API_KEY } from './api'
import axios from 'axios'

const BASE_API_URL = API_BASE_URL

const handleAxiosError = (
  error: unknown,
  action: string,
  extras?: Record<string, unknown>,
) => {
  if (axios.isAxiosError(error)) {
    Sentry.captureException(error, {
      extra: {
        action,
        ...extras,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: action,
      },
    })
    throw error.response?.data || error
  } else {
    throw error
  }
}

const fetchAnnounces = async (): Promise<any[]> => {
  const FULL_API_URL = `${BASE_API_URL}/all/full`

  try {
    const response = await fetch(FULL_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch data')
    }
    return await response.json()
  } catch (error) {
    handleAxiosError(error, 'fetchAnnounces')
    return []
  }
}

const fetchGalleryByUserId = async (userId: number): Promise<string> => {
  const GALLERY_API_URL = `${BASE_API_URL}/${userId}/gallery`

  try {
    const response = await fetch(GALLERY_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch gallery')
    }
    const galleryData = await response.json()
    return `${API_BASE_URL}/images/${galleryData.fileName}`
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchGalleryByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchGalleryByUserId',
      },
    })
    throw error
  }
}

const postMyAnnounce = async (
  myAnnounce: any,
  token: string,
  userId: string,
): Promise<any> => {
  // ✅ On retourne les données
  const POST_API_URL = `${BASE_API_URL}/user/${userId}/libertin`
  const requestBody = {
    ...myAnnounce,
    email: myAnnounce.email || false,
    userId: userId,
    smsOnly: myAnnounce.smsOnly || false,
    callOnly: myAnnounce.callOnly || false,
    whatsapp: myAnnounce.whatsapp || false,
    emailOnly: myAnnounce.emailOnly || false,
    escort: myAnnounce.escort || false,
    infidelityCard: myAnnounce.infidelityCard || false,
    practices: myAnnounce.practices || [],
    disponibility: myAnnounce.disponibility || [],
    status: myAnnounce.status || [],
    location: Array.isArray(myAnnounce.location) ? myAnnounce.location : [],
    views: myAnnounce.views || 0,
    website: myAnnounce.website || '',
  }

  console.log('📤 Creating announce - requestBody:', requestBody)

  try {
    const response = await axios.post(POST_API_URL, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    console.log('✅ Announce created successfully:', response.data)
    return response.data // ✅ On retourne les données
  } catch (error) {
    handleAxiosError(error, 'postMyAnnounce', { userId, requestBody })
    throw error
  }
}

const fetchAnnounceByUserId = async (userId: string, token: string) => {
  const url = `${BASE_API_URL}/user/${userId}/libertin`

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch announce')
    }
    return await response.json()
  } catch (error) {
    handleAxiosError(error, 'fetchAnnounceByUserId', { userId })
  }
}

const fetchAnnounceLibertinByUserId = async (userId: string, token: string) => {
  const url = `${BASE_API_URL}/user/${userId}/libertin`
  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error('Failed to fetch announce')
    }
    const data = await response.json()
    return data.libertin
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAnnounceLibertinByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAnnounceLibertinByUserId',
      },
    })
    throw error
  }
}

const updateLocalStorageWithUserAnnounce = (userAnnounces: any) => {
  if (
    !userAnnounces ||
    (Array.isArray(userAnnounces) && userAnnounces.length === 0)
  ) {
    localStorage.setItem('myAnnounce', JSON.stringify({}))
  } else {
    localStorage.setItem('myAnnounce', JSON.stringify(userAnnounces))
  }
}

const getUserAnnounce = async (userId: string, token: string): Promise<any> => {
  const USER_ANNOUNCE_API_URL = `${BASE_API_URL}/user/${userId}/libertin`

  try {
    const response = await fetch(USER_ANNOUNCE_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch user announce')
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'getUserAnnounce',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'getUserAnnounce',
      },
    })
    throw error
  }
}

const updateMyAnnounce = async (
  myAnnounce: any,
  token: string,
  userId: string,
): Promise<any> => {
  const UPDATE_API_URL = `${BASE_API_URL}/user/${userId}/libertin`

  // ✅ Récupérer les données actuelles dans le localStorage
  const oldAnnounce = JSON.parse(localStorage.getItem('myAnnounce') || '{}')

  const requestBody = {
    ...myAnnounce,
    email: myAnnounce.email || false,
    userId: userId,
    smsOnly: myAnnounce.smsOnly || false,
    callOnly: myAnnounce.callOnly || false,
    whatsapp: myAnnounce.whatsapp || false,
    emailOnly: myAnnounce.emailOnly || false,
    escort: myAnnounce.escort || false,
    infidelityCard: myAnnounce.infidelityCard || false,
    practices: myAnnounce.practices ? [...myAnnounce.practices] : [],

    disponibility: myAnnounce.disponibility?.length
      ? myAnnounce.disponibility
      : oldAnnounce.disponibility,
    availabilityStart: myAnnounce.availabilityStart?.length
      ? myAnnounce.availabilityStart
      : oldAnnounce.availabilityStart,
    availabilityEnd: myAnnounce.availabilityEnd?.length
      ? myAnnounce.availabilityEnd
      : oldAnnounce.availabilityEnd,
    location: Array.isArray(myAnnounce.location)
      ? myAnnounce.location
      : oldAnnounce.location,
    views: myAnnounce.views || oldAnnounce.views || 0,
    website: myAnnounce.website || oldAnnounce.website || '',
  }

  console.log('🚀 FINAL API CALL - requestBody:', requestBody)

  try {
    const response = await axios.patch(UPDATE_API_URL, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    console.log('✅ Announce updated successfully:', response.data)
    return response.data.libertin
  } catch (error) {
    handleAxiosError(error, 'updateMyAnnounce', { userId, requestBody })
    throw error
  }
}

interface Announce {
  id: number
  userId: number
  title: string
}

const fetchAnnounce = async (): Promise<Announce[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/libertins`, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch announces')
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAnnounce',
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAnnounce',
      },
    })
    throw error
  }
}

const deleteMyLibertin = async (announceId: number | string, token: string) => {
  const url = `${BASE_API_URL}/user/${announceId}/libertin`

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to delete announce')
    }
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'deleteMyLibertin',
        announceId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'deleteMyLibertin',
      },
    })
    throw error
  }
}

const fetchAnnounceDetails = async (id: string): Promise<any> => {
  const ANNOUNCE_API_URL = `${BASE_API_URL}/libertins/${id}`

  try {
    const response = await fetch(ANNOUNCE_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch announce with id ${id}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAnnounceDetails',
        id,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAnnounceDetails',
      },
    })
    throw error
  }
}

export {
  fetchAnnounces,
  fetchAnnounceDetails,
  fetchAnnounce,
  updateMyAnnounce,
  fetchAnnounceByUserId,
  fetchGalleryByUserId,
  postMyAnnounce,
  fetchAnnounceLibertinByUserId,
  updateLocalStorageWithUserAnnounce,
  getUserAnnounce,
  deleteMyLibertin,
}
export type { Announce }
